import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { lessonsApi } from "../lib/lessons";

function defaultStart(): string {
  const d = new Date(Date.now() + 30 * 60 * 1000);
  d.setSeconds(0, 0);
  // datetime-local format yyyy-MM-ddTHH:mm
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function NewLessonPage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [startsAtStr, setStartsAtStr] = useState<string>(defaultStart);
  const [durationMin, setDurationMin] = useState(60);
  const [maxParticipants, setMaxParticipants] = useState(8);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return <Link to="/" className="text-white p-6 block">Zaloguj się</Link>;

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const startsAt = new Date(startsAtStr).getTime();
      if (Number.isNaN(startsAt)) throw new Error("Nieprawidłowa data startu");
      const endsAt = startsAt + durationMin * 60 * 1000;
      const { lesson } = await lessonsApi.create({
        title: title.trim(),
        starts_at: startsAt,
        ends_at: endsAt,
        max_participants: maxParticipants,
      });
      navigate(`/lessons/${lesson.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="h-full bg-ink-900 text-white">
      <header className="px-6 py-4 border-b border-ink-700">
        <Link to="/" className="text-xs text-ink-400 hover:text-white">← Wróć</Link>
        <h1 className="text-lg font-semibold mt-1">Nowa lekcja</h1>
      </header>
      <main className="max-w-md mx-auto p-6 space-y-4">
        <Field label="Tytuł">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="np. Angielski B1 — present perfect"
            className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 focus:outline-none focus:border-lime-500"
          />
        </Field>
        <Field label="Początek">
          <input
            type="datetime-local"
            value={startsAtStr}
            onChange={(e) => setStartsAtStr(e.target.value)}
            className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 focus:outline-none focus:border-lime-500"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Czas (min)">
            <input
              type="number"
              min={15}
              step={15}
              value={durationMin}
              onChange={(e) => setDurationMin(Number(e.target.value))}
              className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 focus:outline-none focus:border-lime-500"
            />
          </Field>
          <Field label="Max uczestników">
            <input
              type="number"
              min={2}
              max={8}
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(Number(e.target.value))}
              className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 focus:outline-none focus:border-lime-500"
            />
          </Field>
        </div>
        {error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-3 py-2 text-red-200 text-sm">
            {error}
          </div>
        )}
        <button
          disabled={!title.trim() || submitting}
          onClick={submit}
          className="w-full bg-lime-500 hover:bg-lime-400 disabled:bg-ink-700 text-ink-900 font-semibold rounded-lg py-2.5"
        >
          {submitting ? "Tworzę…" : "Utwórz lekcję"}
        </button>
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="text-xs text-ink-400 uppercase tracking-wider">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
